import { Box, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { FC, ReactNode, useEffect, useState } from 'react';
import { getAllCV, IJob, convertWordToPdf } from 'wisestep-api';
import {
  WsColors,
  WsDialog,
  Item,
  IConversationState,
  ICandidateAggregatedData,
  WsTypography,
} from 'wisestep-base';

import { ResumeDetails } from '../../../features/candidates/utils/CandidateHelper';
import GlobalLoader from '../../../interceptor/useAxiosLoader';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';

import ConversationResumeView from './ConversationResumeView';
import ConversationWidgetFooter from './ConversationWidgetFooter';
import ConversationWidgetHeader from './ConversationWidgetHeader';

const Header = styled(Grid)(() => ({
  backgroundColor: WsColors.TertiaryShades200,
}));

const Content = styled(Grid)(() => ({
  height: '90vw',
  overflow: 'auto',
}));

interface IConversationWidgetLayout {
  aggregation: ICandidateAggregatedData | null;
  state: IConversationState;
  selectedJob: IJob | null;
  children: ReactNode;
  update: boolean;
}

const ConversationWidgetLayout: FC<IConversationWidgetLayout> = ({
  aggregation,
  state,
  selectedJob,
  children,
  update = false,
}) => {
  const [resume, setResume] = useState<ResumeDetails[]>([]);
  const [resumeUrl, setResumeUrl] = useState<string>('');
  const [pdfResumeUrl, setPdfResumeUrl] = useState<string>();
  const view = useConversationWidgetStore((state) => state.view);
  const [isCVCheckDone, setIsCVCheckDone] = useState(false);
  const fromPage = useConversationWidgetStore((state) => state.fromPage);
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const setLatestResume = useConversationWidgetStore((state) => state.setLatestResume);
  const [isChange, setIsChange] = useState<boolean>(true);

  useEffect(() => {
    if (update) {
      setResume([]);
      setResumeUrl('');
      setIsCVCheckDone(false);
    }
  }, [update]);

  useEffect(() => {
    const convertWordResumeToPdf = async (resumeUrl: string) => {
      if (resumeUrl?.split('.').pop() === 'doc' || resumeUrl?.split('.').pop() === 'docx') {
        const response = await convertWordToPdf(resumeUrl);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);
        setPdfResumeUrl(pdfUrl);
      }
    };
    (async () => {
      if (aggregation?.candidateEntity?.candidateId && !isCVCheckDone && view != 'submission') {
        const res = await getAllCV(aggregation?.candidateEntity.candidateId);
        const tempResume: ResumeDetails[] = [];
        res.data.map((candidateResume) =>
          tempResume.push({
            url: candidateResume.resumeLocation,
            fileName: candidateResume.resumeFileOriginalName,
            resumeUpdatedOn: candidateResume.resumeUploadedOn
              ? candidateResume.resumeUploadedOn.toString()
              : '',
            s3FileName: candidateResume.s3FileName,
          })
        );

        if (!isEmpty(tempResume)) {
          setResumeUrl(tempResume[0].url);
          if (searchQuery && tempResume[0]?.url?.split('.').pop() === 'html') {
            setIsChange(false);
          }
          try {
            await convertWordResumeToPdf(tempResume[0].url);
          } catch (e) {
            console.log(e);
          }
          setLatestResume({
            name: tempResume[0].fileName,
            url: tempResume[0].url,
          });
          setResume([...tempResume]);
        }
        setIsCVCheckDone(true);
      } else if (
        view === 'submission' &&
        !isCVCheckDone &&
        state?.candidate?.resumeFileOriginalName
      ) {
        if (state.candidate.resumeLocation) {
          setResumeUrl(state.candidate.resumeLocation);
          if (searchQuery && state?.candidate?.resumeLocation?.split('.').pop() === 'html') {
            setIsChange(false);
          }
          try {
            await convertWordResumeToPdf(state.candidate.resumeLocation);
          } catch (e) {
            console.log(e);
          }
          setResume([
            {
              url: state.candidate.resumeLocation,
              fileName: state.candidate.resumeFileOriginalName,
              resumeUpdatedOn: state.candidate.resumeUploadedOn
                ? state.candidate.resumeUploadedOn.toString()
                : '',
              s3FileName: state.candidate.resumeFileOriginalName,
            },
          ]);
        } else if (aggregation?.candidateEntity?.candidateId) {
          const res = await getAllCV(aggregation?.candidateEntity.candidateId);
          const resumes = res.data.filter(
            (cv) => cv.resumeFileOriginalName === state.candidate.resumeFileOriginalName
          );
          if (!isEmpty(resumes)) {
            setResumeUrl(resumes[0].resumeLocation);
            if (searchQuery && resumes[0]?.resumeLocation?.split('.').pop() === 'html') {
              setIsChange(false);
            }
            try {
              await convertWordResumeToPdf(resumes[0].resumeLocation);
            } catch (e) {
              console.log(e);
            }
            setResume([
              {
                url: resumes[0].resumeLocation,
                fileName: resumes[0].resumeFileOriginalName,
                resumeUpdatedOn: resumes[0].resumeUploadedOn
                  ? resumes[0].resumeUploadedOn.toString()
                  : '',
                s3FileName: resumes[0].resumeFileOriginalName,
              },
            ]);
          } else if (!isEmpty(res.data)) {
            setResumeUrl(res.data[0].resumeLocation);
            if (searchQuery && res?.data[0]?.resumeLocation?.split('.').pop() === 'html') {
              setIsChange(false);
            }
            try {
              await convertWordResumeToPdf(res.data[0].resumeLocation);
            } catch (e) {
              console.log(e);
            }
            setResume([
              {
                url: res.data[0].resumeLocation,
                fileName: res.data[0].resumeFileOriginalName,
                resumeUpdatedOn: res.data[0].resumeUploadedOn
                  ? res.data[0].resumeUploadedOn.toString()
                  : '',
                s3FileName: res.data[0].resumeFileOriginalName,
              },
            ]);
          }
        }
        setIsCVCheckDone(true);
      } else {
        setIsCVCheckDone(true);
      }
    })();
  }, [
    aggregation?.candidateEntity?.candidateId,
    isCVCheckDone,
    resume,
    setLatestResume,
    state.candidate.resumeFileOriginalName,
    state.candidate.resumeLocation,
    state.candidate.resumeUploadedOn,
    view,
  ]);

  const changeResume = async (resumeUrl: string) => {
    try {
      await convertWordResumeToPdf(resumeUrl);
    } catch (e) {
      console.log(e);
    }
    setResumeUrl(resumeUrl);
  };
  const changeView = async (ischange: boolean) => {
    if (ischange) {
      try {
        await convertWordResumeToPdf(resumeUrl);
      } catch (e) {
        console.log(e);
      }
    }
    setIsChange(ischange);
  };

  const convertWordResumeToPdf = async (resumeUrl: string) => {
    if (resumeUrl?.split('.').pop() === 'doc' || resumeUrl?.split('.').pop() === 'docx') {
      const response = await convertWordToPdf(resumeUrl);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);
      setPdfResumeUrl(pdfUrl);
    }
  };
  return (
    <WsDialog openDialog={true} fullScreen={true}>
      <Box height={5}>
        <GlobalLoader />
      </Box>
      <ConversationWidgetHeader
        aggregation={aggregation}
        job={selectedJob}
        state={state}
        candidateResumes={resume}
        changeResume={changeResume}
        changeView={changeView}
      />
      <Content container>
        <Header item md={6} sx={{ overflowY: 'auto', height: '100%' }}>
          {isCVCheckDone ? (
            <ConversationResumeView
              resumeLocation={resumeUrl}
              isOriginaFormatView={isChange}
              pdfResumeUrl={pdfResumeUrl}
              job={selectedJob}
            />
          ) : (
            <Item sx={{ height: '100%', bgcolor: WsColors.White, marginRight: 1 }}>
              <Stack direction="row" justifyContent="center" alignItems="center">
                <WsTypography>Loading CV...</WsTypography>
              </Stack>
            </Item>
          )}
        </Header>
        <Grid item md={6} sx={{ overflowY: 'auto', height: '100%' }}>
          <Item>{children}</Item>
        </Grid>
      </Content>
      {state.candidate.isInDnc || fromPage == 'closure' ? null : (
        <ConversationWidgetFooter
          state={state}
          selectedJob={selectedJob}
          aggregation={aggregation}
        />
      )}
    </WsDialog>
  );
};
export default ConversationWidgetLayout;
